import React, {Component} from 'react';
import PropTypes from 'prop-types';
import {ButtonPrimary} from '@homeaway/react-buttons';
import {FormInput} from '@homeaway/react-form-components';
import {InlineMessage} from '@homeaway/react-alerts';

/**
 * @extends {Component<NewsletterSignupProps, NewsletterSignupState>}
 */
export default class NewsletterSignup extends Component {
    static propTypes = {
        /**
         * The url to submit the request
         */
        dataUrl: PropTypes.string,
        /**
         * The translations
         */
        i18n: PropTypes.shape({
            buttonLabel: PropTypes.string,
            failure: PropTypes.string,
            info: PropTypes.string,
            inputLabel: PropTypes.string,
            invalidEmail: PropTypes.string,
            success: PropTypes.string,
        }).isRequired,
        /**
         * The site name
         */
        site: PropTypes.string,
        /**
         * The name of the submitting application or component
         */
        source: PropTypes.string.isRequired,
        /**
         * The url from where the email signup was submitted
         */
        sourceUrl: PropTypes.string,
    };

    static defaultProps = {
        dataUrl: '/newsletter-subscriptions/v1/subscriptions',
        i18n: {},
        site: 'homeaway_us',
    };

    state = {
        emailInputValue: '',
        hasSubmitError: false,
        hasSubmitSuccess: false,
        isEmailInvalid: false,
        isSubmitting: false,
    };

    emailRegex = /^[^@]+@[^.]+[.].+$/;

    get message() {
        const {i18n} = this.props;
        let msg = null;

        if (this.state.isEmailInvalid) {
            msg = <InlineMessage id="NewsletterSignup__message" marginTop={8} type="warn" message={i18n.invalidEmail} />;
        } else if (this.state.hasSubmitError) {
            msg = <InlineMessage id="NewsletterSignup__message" marginTop={8} type="danger" message={i18n.failure} />;
        } else if (this.state.hasSubmitSuccess) {
            msg = <InlineMessage id="NewsletterSignup__message" marginTop={8} type="success" message={i18n.success} />;
        }

        return msg;
    }

    get inputState() {
        return this.state.emailInputValue;
    }

    set inputState(emailInputValue) {
        this.setState({emailInputValue});
    }

    /**
     * @param {React.ChangeEvent<HTMLInputElement>} event
     */
    handleInputChange = (event) => {
        this.inputState = event.target.value;
    }

    handleSubmit = (event) => {
        event.preventDefault();

        this.setState({
            hasSubmitError: false,
            isEmailInvalid: false,
            isSubmitting: true,
        }, () => {
            if (this.isValidEmail(this.inputState)) {
                this.submit(this.inputState);
            } else {
                this.setState({
                    isEmailInvalid: true,
                    isSubmitting: false,
                });
            }
        });
    }

    /**
     * @param {string} email
     */
    isValidEmail = (email) => this.emailRegex.test(email);

    /**
     * @param {string} emailAddress
     *
     * @returns {Promise<Response>}
     */
    submit = (emailAddress) => {
        const {
            dataUrl,
            site,
            source,
            sourceUrl = window.location.href,
        } = this.props;

        return fetch(dataUrl, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                emailAddress,
                site,
                source,
                sourceUrl,
            }),
        }).then((response) => {
            if (response.status >= 400) {
                throw `${response.status}: ${response.statusText}`;
            }

            this.setState({
                hasSubmitSuccess: true,
                isSubmitting: false,
            });

            return response;
        }).catch(/* istanbul ignore next */(error) => {
            console.log(error); // eslint-disable-line no-console

            this.setState({
                hasSubmitError: true,
                isSubmitting: false,
            });

            return error;
        });
    }

    render() {
        const {
            i18n,
            source,
        } = this.props;


        if (!source) {
            return null;
        }

        return (
            <div className="NewsletterSignup">
                <h3>{i18n.info}</h3>

                <form onSubmit={this.handleSubmit}>
                    <div className="input-group">
                        <div className="input-group-wrapper">
                            <FormInput
                                id={'NewsletterSignup__input'}
                                label={i18n.inputLabel}
                                onChange={this.handleInputChange}
                                type={'email'}
                                value={this.state.emailInputValue}
                            />
                        </div>
                        <div className="input-group-btn">
                            <ButtonPrimary
                                disabled={this.state.isSubmittingEmail}
                                onClick={this.handleSubmit}
                                label={i18n.buttonLabel}
                            />
                        </div>
                    </div>
                    {this.message}
                </form>
            </div>
        );
    }
}
